import React, { useEffect, useState, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { XperienceMedia, getMediaForModeration, updateMediaModerationStatus } from '../../services/api';
import { t } from '../../i18n';
import './ModerationPage.css';

const ModerationPage: React.FC = () => {
  const { creator } = useAuth();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const targetXperienceId = searchParams.get('xperienceId');

  const [mediaList, setMediaList] = useState<XperienceMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'pending' | 'approved' | 'rejected' | ''>('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [feedbackMsg, setFeedbackMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    fetchMedia();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, targetXperienceId]);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      const response = await getMediaForModeration(targetXperienceId || undefined, statusFilter);
      setMediaList(response.data.media);
    } catch (error) {
      console.error('Error fetching media:', error);
      setFeedbackMsg({ type: 'error', text: t('moderation.error.loadMedia') });
    } finally {
      setLoading(false);
    }
  };

  const filteredMediaList = useMemo(() => {
    let list = mediaList;

    // First ensure we only show media for the selected xperience if we're filtering by one
    if (targetXperienceId) {
      list = list.filter(media => media.xperience === targetXperienceId);
    }

    if (!searchQuery.trim()) return list;
    
    const lowerQuery = searchQuery.toLowerCase();
    
    return list.filter(media => {
      const mediaTitleMatch = media.title?.toLowerCase().includes(lowerQuery) || false;
      const mediaDescMatch = media.description?.toLowerCase().includes(lowerQuery) || false;
      const xperienceTitleMatch = media.Xperience?.title?.toLowerCase().includes(lowerQuery) || false;
      
      const creatorData = media.Xperience?.creatorUser;
      const creatorMatch = 
        (creatorData?.name?.toLowerCase().includes(lowerQuery) || false) || 
        (creatorData?.alias?.toLowerCase().includes(lowerQuery) || false) || 
        (creatorData?.email?.toLowerCase().includes(lowerQuery) || false) ||
        (media.creator?.toLowerCase().includes(lowerQuery) || false);
                           
      return mediaTitleMatch || mediaDescMatch || xperienceTitleMatch || creatorMatch;
    });
  }, [mediaList, searchQuery, targetXperienceId]);

  const handleStatusChange = async (id: string, newStatus: 'pending' | 'approved' | 'rejected') => {
    try {
      await updateMediaModerationStatus(id, newStatus);
      setFeedbackMsg({ type: 'success', text: `${t('moderation.success.markedAs')} ${t(`moderation.filter.${newStatus}`)}` });
      // Reload current list or filter out locally
      if (statusFilter === newStatus || statusFilter === '') {
        setMediaList(prev => prev.map(m => m.id === id ? { ...m, status: newStatus } : m));
      } else {
        setMediaList(prev => prev.filter(m => m.id !== id));
      }
      setTimeout(() => setFeedbackMsg(null), 3000);
    } catch (error) {
      console.error('Error updating status:', error);
      setFeedbackMsg({ type: 'error', text: t('moderation.error.updateStatus') });
    }
  };

  if (!creator || !['manager', 'administrator'].includes(creator.role || '')) {
    return <Navigate to="/" />;
  }

  return (
    <div className="moderation-page">
      <div className="moderation-header">
        <div style={{ marginBottom: '20px' }}>
          <button 
            onClick={() => window.history.back()} 
            style={{
              background: 'transparent', 
              border: '1px solid var(--border-color)', 
              color: 'var(--text-secondary)', 
              padding: '8px 16px', 
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: 500
            }}
          >
            ← {t('moderation.back')}
          </button>
        </div>
        <h1>{targetXperienceId ? t('moderation.title.single') : t('moderation.title.global')}</h1>
        
        <div className="moderation-controls">
          <div className="search-bar" style={{ position: 'relative' }}>
            <input 
              type="text" 
              placeholder={t('moderation.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
              style={{ paddingRight: '36px' }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontSize: '16px',
                  padding: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title={t('common.clearSearch')}
              >
                ✕
              </button>
            )}
          </div>

          <div className="moderation-filters">
            <button 
              className={`filter-btn ${statusFilter === '' ? 'active' : ''}`}
              onClick={() => setStatusFilter('')}
            >
              {t('moderation.filter.allMedia')}
            </button>
            <button 
              className={`filter-btn ${statusFilter === 'pending' ? 'active' : ''}`}
              onClick={() => setStatusFilter('pending')}
            >
              {t('moderation.filter.pending')}
            </button>
            <button 
              className={`filter-btn ${statusFilter === 'approved' ? 'active' : ''}`}
              onClick={() => setStatusFilter('approved')}
            >
              {t('moderation.filter.approved')}
            </button>
            <button 
              className={`filter-btn ${statusFilter === 'rejected' ? 'active' : ''}`}
              onClick={() => setStatusFilter('rejected')}
            >
              {t('moderation.filter.rejected')}
            </button>
          </div>
        </div>
      </div>

      {feedbackMsg && (
        <div className={`feedback-message ${feedbackMsg.type}`}>
          {feedbackMsg.text}
        </div>
      )}

      {loading ? (
        <div className="loading-state">{t('moderation.loading')}</div>
      ) : filteredMediaList.length === 0 ? (
        <div className="empty-state">
          <p>{mediaList.length > 0 ? t('moderation.empty.search') : t('moderation.empty.filter')}</p>
        </div>
      ) : (
        <div className="media-moderation-grid">
          {filteredMediaList.map((media) => (
            <div key={media.id} className={`moderation-card status-${media.status}`}>
              <div 
                className="media-thumbnail" 
                style={{ backgroundImage: `url(${media.posterUrl ? `https://x-perienc.es/${media.posterUrl}` : ''})` }}
              >
                {!media.posterUrl && <div className="no-poster">{t('moderation.noPoster')}</div>}
                <div className="status-badge">{t(`moderation.filter.${media.status}`)}</div>
              </div>
              
              <div className="media-info">
                <h3>{media.Xperience?.title ? `${media.Xperience.title} - ${media.title || t('moderation.untitled')}` : (media.title || t('moderation.untitled'))}</h3>
                <p className="media-desc">{media.description || t('moderation.noDescription')}</p>
                <div className="media-meta">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '8px' }}>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>
                      {t('moderation.creator')}: {media.Xperience?.creatorUser?.name || media.Xperience?.creatorUser?.alias || media.creator}
                    </span>
                    <span style={{ fontSize: '0.85em', color: 'var(--text-muted)' }}>
                      {t('moderation.email')}: {media.Xperience?.creatorUser?.email || t('moderation.na')}
                    </span>
                    <span style={{ fontSize: '0.85em', color: 'var(--text-muted)' }}>
                      {t('moderation.date')}: {media.createdDate ? new Date(media.createdDate).toLocaleDateString() : t('moderation.na')}
                    </span>
                  </div>
                </div>
                
                {media.url && (
                  <a href={`https://x-perienc.es/${media.url}`} target="_blank" rel="noopener noreferrer" className="view-link">
                    {t('moderation.viewOriginal')}
                  </a>
                )}
              </div>
              
              <div className="moderation-actions">
                {media.status !== 'approved' && (
                  <button 
                    className="action-btn approve"
                    onClick={() => handleStatusChange(media.id!, 'approved')}
                  >
                    {t('moderation.action.approve')}
                  </button>
                )}
                {media.status !== 'rejected' && (
                  <button 
                    className="action-btn reject"
                    onClick={() => handleStatusChange(media.id!, 'rejected')}
                  >
                    {t('moderation.action.reject')}
                  </button>
                )}
                {media.status !== 'pending' && (
                  <button 
                    className="action-btn pending"
                    onClick={() => handleStatusChange(media.id!, 'pending')}
                  >
                    {t('moderation.action.setPending')}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ModerationPage;
